import { Squares2X2Icon } from "@heroicons/react/24/outline";
import Modal from "components/ListingImageGallery/components/Modal";
import DetailLoader from "components/Loader/DetailLoader";
import StartRating from "components/StartRating/StartRating";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaCalendarAlt, FaClock, FaFileDownload, FaHome, FaTag } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { adminFetchLocationById } from "redux/features/locations/locationSlice";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import TextDisplay from "utils/textAreaDisplay";
import DetailPagetLayout from "../Layout";

interface Client {
  adresseImg_url?: string;
  iban_url?: string;
  identityBImg_url?: string;
  identityFImg_url?: string;
  companyCertifImg_url?: string;
}

const StayDetailPageContainer: FC<{}> = () => {
  const thisPathname = useLocation().pathname;
  let locationState: any = useAppSelector((state) => state.featureLocation.location);

  let ownerState: any = useAppSelector((state) => state.featureLocation.owner);

  const dispatch = useAppDispatch();
  const router = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [allDescs, setAllDesc] = useState(true);
  const [loading, setLoading] = useState(false);

  const lat = 48.8584;
  const lng = 2.2945;

  const googleMapsURL = `https://www.google.com/maps/embed/v1/place?key=${
    process.env.REACT_APP_MAPS_API_KEY
  }&q=${Number(lat)},${Number(lng)}`;

  const [modalOpen, setModalOpen] = useState(false);
  const [clickedImages, setClickedImages] = useState<string[]>([]);
  const handleImageClick = (imageUrl: string) => {
    setClickedImages((prevImages) => [...prevImages, imageUrl]);
    setModalOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (id) {
          await dispatch(adminFetchLocationById(id));
        }
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
  }, [id, dispatch, locationState?.userId]);

  const [fileTypes, setFileTypes] = useState<Record<string, "image" | "pdf">>({});
  useEffect(() => {
    const determineFileType = (url: string): "image" | "pdf" => {
      return url.endsWith(".pdf") ? "pdf" : "image";
    };
    const urls: Array<keyof Client> = [
      "identityFImg_url",
      "identityBImg_url",
      "iban_url",
      "adresseImg_url",
      "companyCertifImg_url",
    ];
    const types = urls.reduce(
      (acc, key) => {
        const url = locationState?.Client?.[key];
        if (url) acc[key] = determineFileType(url);
        return acc;
      },
      {} as Record<keyof Client, "image" | "pdf">
    );
    setFileTypes(types);
  }, [locationState]);

  const handleDownload = (pdfLink: any) => {
    const link = document.createElement("a");
    link.href = process.env.REACT_APP_CLOUD_FRONT + pdfLink;
    link.target = "_blank";
    link.download = "file.pdf";
    link.click();
  };

  const handleOpenModalImageGallery = () => {
    router(`${thisPathname}/?modal=PHOTO_TOUR_SCROLLABLE`);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  const renderSection1 = () => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
    >
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between mb-4">
            <Badge
              name={
                <div className="flex items-center">
                  <i className="mr-1 text-sm las la-share-alt"></i>
                  <span>{locationState?.CategoryLocation?.designation}</span>
                </div>
              }
              className="text-blue-800 bg-blue-100"
            />
            <Badge
              name={
                <div className="flex items-center">
                  <i className="mr-1 las la-map-marker"></i>
                  <span>{locationState?.city}</span>
                </div>
              }
              color="yellow"
              className="text-yellow-800 bg-yellow-100"
            />
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">{locationState?.title}</h2>
          <div className="flex items-center mb-4 space-x-4">
            <StartRating point={locationState?.averageLikes} reviewCount={locationState?.commentCount} />
            <span className="text-gray-500 dark:text-gray-400">·</span>
            <span className="flex items-center text-gray-600 dark:text-gray-300">
              <i className="mr-1 las la-map-marker-alt"></i>
              {locationState?.city}, {locationState?.codePostale}
              {location.search !== "" && `, ${locationState?.address} ${locationState?.address2}`}
            </span>
          </div>
          <div className="flex items-center mb-4">
            <a href={`/owners/${locationState?.Client?.id}`}>
              <Avatar
                hasChecked
                sizeClass="h-10 w-10"
                radius="rounded-full"
                imgUrl={
                  locationState?.Client?.client_url
                    ? process.env.REACT_APP_CLOUD_FRONT + locationState?.Client?.client_url
                    : ""
                }
              />
            </a>
            <span className="ml-3 text-gray-600 dark:text-gray-300">
              Publié par :{" "}
              <span className="font-semibold text-gray-900 dark:text-white">{locationState?.Client?.name}</span>
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 text-gray-600 sm:grid-cols-2 dark:text-gray-300">
            <div className="flex items-center space-x-3">
              <i className="text-xl las la-expand"></i>
              <span>Superficie {locationState?.surface} m²</span>
            </div>
            <div className="flex items-center space-x-3">
              <i className="text-xl las la-user"></i>
              <span>Capacité {locationState?.visitor}</span>
            </div>
            <div className="flex items-center space-x-3">
              <i className="text-xl las la-clock"></i>
              <span>
                {String(locationState?.minStartHour).padStart(2, "0")}:00 -{" "}
                {String(locationState?.maxEndHour).padStart(2, "0")}:00
              </span>
            </div>
            {locationState?.handicap !== false && (
              <div className="flex items-center space-x-3">
                <i className="text-xl lab la-accessible-icon"></i>
                <span>Accessible</span>
              </div>
            )}
            <div className="flex items-center space-x-3">
              <i className="text-xl las la-toilet"></i>
              <span>WC {locationState?.wc}</span>
            </div>
            <div className="flex items-center space-x-3">
              <i className="text-xl las la-parking"></i>
              <span>Parking {locationState?.parking}</span>
            </div>
            {locationState?.water && (
              <div className="flex items-center space-x-3">
                <i className="text-xl las la-shower"></i>
                <span>Point d'eau</span>
              </div>
            )}
            {locationState?.light && (
              <div className="flex items-center space-x-3">
                <i className="text-xl las la-lightbulb"></i>
                <span>Éclairage nocturne</span>
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );

  const renderSection2 = () => {
    const desc = locationState?.description.slice(0, 675);
    const handleClickVoirPlus = () => setAllDesc(false);
    const handleClickVoirMoins = () => setAllDesc(true);

    return (
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
      >
        <AnimatePresence>
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-wrap gap-2 mb-4">
              {locationState?.atmosphere.map((item: any, index: number) => (
                <Badge
                  key={index}
                  href="##"
                  color="purple"
                  name={item}
                  className="text-purple-800 bg-purple-100"
                />
              ))}
            </div>
            <div className="text-gray-600 dark:text-gray-300">
              <TextDisplay text={allDescs ? desc : locationState?.description} />
              {locationState?.description.length >= 675 && allDescs ? (
                <span
                  className="text-sm text-blue-500 cursor-pointer hover:underline"
                  onClick={handleClickVoirPlus}
                >
                  Voir plus
                </span>
              ) : (
                !allDescs && (
                  <span
                    className="text-sm text-blue-500 cursor-pointer hover:underline"
                    onClick={handleClickVoirMoins}
                  >
                    Voir moins
                  </span>
                )
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  };

  const RenderSection = ({
    title,
    items,
    iconClass,
    renderItem,
    sectionKey,
  }: {
    title: string;
    items: any[];
    iconClass: string;
    renderItem: (item: any) => React.ReactNode;
    sectionKey: string;
  }) => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
      </div>
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 gap-4 text-gray-600 sm:grid-cols-2 dark:text-gray-300"
        >
          {items?.map((item: any, index: number) => (
            <div key={index} className="flex items-center space-x-3">
              <i className={`text-xl ${iconClass}`}></i>
              <span>{renderItem(item)}</span>
            </div>
          ))}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );

  const renderSection3 = () => (
    <RenderSection
      title="Équipements Disponibles"
      items={locationState?.equipments}
      iconClass="las la-check"
      renderItem={(item: any) => item?.equipment}
      sectionKey="equipments"
    />
  );

  const renderEventsSection = () => (
    <RenderSection
      title="Événements"
      items={locationState?.events}
      iconClass="las la-handshake"
      renderItem={(item: any) => item}
      sectionKey="events"
    />
  );

  const renderPlayGroundsSection = () => (
    <RenderSection
      title="Aire de jeux"
      items={locationState?.playground}
      iconClass="las la-dice"
      renderItem={(item: any) => item}
      sectionKey="playground"
    />
  );

  const renderSection9 = () => (
    <RenderSection
      title="Les règles de cet espace"
      items={locationState?.conditions}
      iconClass="las la-exclamation-triangle"
      renderItem={(item: any) => item?.designation}
      sectionKey="conditions"
    />
  );

  const renderSection10 = () => {
    const extras = locationState?.extras || [];

    return (
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Extra équipements</h2>
          </div>
        </div>
        <AnimatePresence>
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="text-gray-600 dark:text-gray-300"
          >
            {extras.length > 0 ? (
              extras.map((item: any, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between px-3 py-3 transition-colors duration-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center space-x-3">
                    <div className="las la-check" />
                    <span className="text-gray-900 dark:text-white">{item.designation}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {item.price !== 0 ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600 dark:text-gray-300">{item.price} € </span>
                        <FaTag className="text-blue-400 " />
                        <span className="text-gray-600 dark:text-gray-300">({item.quantity} unités)</span>
                      </div>
                    ) : (
                      <span className="text-blue-500 dark:text-blue-400">Gratuit</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Aucun équipement extra disponible.</p>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  };

  const renderSection5 = () => {
    const generateId = () => parseInt(Math.random().toString(10).substr(2, 9));
    return (
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Proposé par</h2>
        </div>
        <AnimatePresence>
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center mb-4 space-x-4">
              <a href={`/owners/${ownerState?.id}`}>
                <Avatar
                  hasChecked
                  hasCheckedClass="w-4 h-4 -top-0.5 right-0.5"
                  sizeClass="h-14 w-14"
                  radius="rounded-full"
                  imgUrl={ownerState?.client_url ? process.env.REACT_APP_CLOUD_FRONT + ownerState?.client_url : ""}
                />
              </a>
              <div>
                <span className="font-semibold text-gray-900 dark:text-white">{ownerState?.name}</span>
                <div className="mt-1.5 flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <StartRating point={ownerState?.overallAverageLikes} />
                  <span className="mx-2">·</span>
                  {ownerState?.totalLocations} Espaces
                </div>
              </div>
            </div>
            <div className="mb-4 space-y-2 text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>Inscrit le {new Date(ownerState?.createdAt ?? "").toLocaleDateString()}</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                  />
                </svg>
                <span>Temps de réponse - 100%</span>
              </div>
            </div>
            <ButtonSecondary href={`/owners/${locationState?.Client?.id}`} className="mb-4">
              Voir le profil
            </ButtonSecondary>

            {locationState?.Client?.account_type === "company" && (
              <div className="space-y-6">
                {/* Company Information */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="p-4 shadow-sm bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom d'entreprise</span>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                      {locationState?.Client?.companyName || "N/A"}
                    </p>
                  </div>
                  <div className="p-4 shadow-sm bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      SIREN d'entreprise
                    </span>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                      {locationState?.Client?.companySiren || "N/A"}
                    </p>
                  </div>
                  <div className="p-4 shadow-sm bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      E-mail d'entreprise
                    </span>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                      {locationState?.Client?.companyEmail || "N/A"}
                    </p>
                  </div>
                  <div className="p-4 shadow-sm bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Téléphone d'entreprise
                    </span>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                      {locationState?.Client?.companyPhone || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Documents */}
                <div className="grid grid-cols-1 gap-6 mt-6 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="p-4 transition-shadow duration-300 bg-white shadow-md dark:bg-gray-800 rounded-xl hover:shadow-lg">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Compte Bancaire (IBAN)
                    </span>
                    {fileTypes["iban_url"] === "image" ? (
                      <img
                        className="object-cover w-full h-40 mt-2 transition-transform duration-200 rounded-lg cursor-pointer hover:scale-105"
                        src={`${process.env.REACT_APP_CLOUD_FRONT}${locationState?.Client?.iban_url}`}
                        alt="Compte Bancaire (IBAN)"
                        onClick={() => handleImageClick(locationState?.Client?.iban_url!)}
                      />
                    ) : fileTypes["iban_url"] === "pdf" ? (
                      <ButtonPrimary
                        onClick={() => handleDownload(locationState?.Client?.iban_url!)}
                        icon={<FaFileDownload />}
                        className="flex items-center justify-center w-full py-2 mt-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Télécharger PDF
                      </ButtonPrimary>
                    ) : (
                      <p className="mt-2 text-sm text-red-500">Type de fichier non supporté</p>
                    )}
                  </div>
                  <div className="p-4 transition-shadow duration-300 bg-white shadow-md dark:bg-gray-800 rounded-xl hover:shadow-lg">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      K-Bis d'entreprise
                    </span>
                    {fileTypes["companyCertifImg_url"] === "image" ? (
                      <img
                        className="object-cover w-full h-40 mt-2 transition-transform duration-200 rounded-lg cursor-pointer hover:scale-105"
                        src={`${process.env.REACT_APP_CLOUD_FRONT}${locationState?.Client?.companyCertifImg_url}`}
                        alt="K-Bis d'entreprise"
                        onClick={() => handleImageClick(locationState?.Client?.companyCertifImg_url!)}
                      />
                    ) : fileTypes["companyCertifImg_url"] === "pdf" ? (
                      <ButtonPrimary
                        onClick={() => handleDownload(locationState?.Client?.companyCertifImg_url!)}
                        icon={<FaFileDownload />}
                        className="flex items-center justify-center w-full py-2 mt-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Télécharger PDF
                      </ButtonPrimary>
                    ) : (
                      <p className="mt-2 text-sm text-red-500">Type de fichier non supporté</p>
                    )}
                  </div>
                  {locationState?.Client?.adresseImg_url && (
                    <div className="p-4 transition-shadow duration-300 bg-white shadow-md dark:bg-gray-800 rounded-xl hover:shadow-lg">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Justificatif à domicile
                      </span>
                      {fileTypes["adresseImg_url"] === "image" ? (
                        <img
                          className="object-cover w-full h-40 mt-2 transition-transform duration-200 rounded-lg cursor-pointer hover:scale-105"
                          src={`${process.env.REACT_APP_CLOUD_FRONT}${locationState?.Client?.adresseImg_url}`}
                          alt="Justificatif à domicile"
                          onClick={() => handleImageClick(locationState?.Client?.adresseImg_url!)}
                        />
                      ) : fileTypes["adresseImg_url"] === "pdf" ? (
                        <ButtonPrimary
                          onClick={() => handleDownload(locationState?.Client?.adresseImg_url!)}
                          icon={<FaFileDownload />}
                          className="flex items-center justify-center w-full py-2 mt-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                        >
                          Télécharger PDF
                        </ButtonPrimary>
                      ) : (
                        <p className="mt-2 text-sm text-red-500">Type de fichier non supporté</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {locationState?.Client?.account_type === "particular" && (
              <div className="space-y-6">
                {/* Individual Information */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="p-4 shadow-sm bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom de la banque</span>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                      {locationState?.Client?.bank || "N/A"}
                    </p>
                  </div>
                  <div className="p-4 shadow-sm bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">RIB</span>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                      {locationState?.Client?.rib || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Documents */}
                <div className="grid grid-cols-1 gap-6 mt-6 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="p-4 transition-shadow duration-300 bg-white shadow-md dark:bg-gray-800 rounded-xl hover:shadow-lg">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Carte d'identité : face avant
                    </span>
                    {fileTypes["identityFImg_url"] === "image" ? (
                      <img
                        className="object-cover w-full h-40 mt-2 transition-transform duration-200 rounded-lg cursor-pointer hover:scale-105"
                        src={`${process.env.REACT_APP_CLOUD_FRONT}${locationState?.Client?.identityFImg_url}`}
                        alt="Carte d'identité : face avant"
                        onClick={() => handleImageClick(locationState?.Client?.identityFImg_url!)}
                      />
                    ) : fileTypes["identityFImg_url"] === "pdf" ? (
                      <ButtonPrimary
                        onClick={() => handleDownload(locationState?.Client?.identityFImg_url!)}
                        icon={<FaFileDownload />}
                        className="flex items-center justify-center w-full py-2 mt-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Télécharger PDF
                      </ButtonPrimary>
                    ) : (
                      <p className="mt-2 text-sm text-red-500">Type de fichier non supporté</p>
                    )}
                  </div>
                  <div className="p-4 transition-shadow duration-300 bg-white shadow-md dark:bg-gray-800 rounded-xl hover:shadow-lg">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Carte d'identité : face arrière
                    </span>
                    {fileTypes["identityBImg_url"] === "image" ? (
                      <img
                        className="object-cover w-full h-40 mt-2 transition-transform duration-200 rounded-lg cursor-pointer hover:scale-105"
                        src={`${process.env.REACT_APP_CLOUD_FRONT}${locationState?.Client?.identityBImg_url}`}
                        alt="Carte d'identité : face arrière"
                        onClick={() => handleImageClick(locationState?.Client?.identityBImg_url!)}
                      />
                    ) : fileTypes["identityBImg_url"] === "pdf" ? (
                      <ButtonPrimary
                        onClick={() => handleDownload(locationState?.Client?.identityBImg_url!)}
                        icon={<FaFileDownload />}
                        className="flex items-center justify-center w-full py-2 mt-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Télécharger PDF
                      </ButtonPrimary>
                    ) : (
                      <p className="mt-2 text-sm text-red-500">Type de fichier non supporté</p>
                    )}
                  </div>
                  <div className="p-4 transition-shadow duration-300 bg-white shadow-md dark:bg-gray-800 rounded-xl hover:shadow-lg">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Compte Bancaire (IBAN)
                    </span>
                    {fileTypes["iban_url"] === "image" ? (
                      <img
                        className="object-cover w-full h-40 mt-2 transition-transform duration-200 rounded-lg cursor-pointer hover:scale-105"
                        src={`${process.env.REACT_APP_CLOUD_FRONT}${locationState?.Client?.iban_url}`}
                        alt="Compte Bancaire (IBAN)"
                        onClick={() => handleImageClick(locationState?.Client?.iban_url!)}
                      />
                    ) : fileTypes["iban_url"] === "pdf" ? (
                      <ButtonPrimary
                        onClick={() => handleDownload(locationState?.Client?.iban_url!)}
                        icon={<FaFileDownload />}
                        className="flex items-center justify-center w-full py-2 mt-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Télécharger PDF
                      </ButtonPrimary>
                    ) : (
                      <p className="mt-2 text-sm text-red-500">Type de fichier non supporté</p>
                    )}
                  </div>
                  <div className="p-4 transition-shadow duration-300 bg-white shadow-md dark:bg-gray-800 rounded-xl hover:shadow-lg">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Justificatif à domicile
                    </span>
                    {fileTypes["adresseImg_url"] === "image" ? (
                      <img
                        className="object-cover w-full h-40 mt-2 transition-transform duration-200 rounded-lg cursor-pointer hover:scale-105"
                        src={`${process.env.REACT_APP_CLOUD_FRONT}${locationState?.Client?.adresseImg_url}`}
                        alt="Justificatif à domicile"
                        onClick={() => handleImageClick(locationState?.Client?.adresseImg_url!)}
                      />
                    ) : fileTypes["adresseImg_url"] === "pdf" ? (
                      <ButtonPrimary
                        onClick={() => handleDownload(locationState?.Client?.adresseImg_url!)}
                        icon={<FaFileDownload />}
                        className="flex items-center justify-center w-full py-2 mt-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Télécharger PDF
                      </ButtonPrimary>
                    ) : (
                      <p className="mt-2 text-sm text-red-500">Type de fichier non supporté</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {modalOpen && (
              <Modal
                images={clickedImages.map((imageUrl) => ({
                  id: generateId(),
                  url: imageUrl,
                }))}
                onClose={() => {
                  setModalOpen(false);
                  setClickedImages([]);
                }}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  };

  const PriceListSection: React.FC<any> = ({ availability = [], prices = [] }) => {
    const [expandedDay, setExpandedDay] = useState<string | null>(null);
    const groupedPrices = prices.reduce((acc: Record<string, any[]>, price: any) => {
      if (!acc[price.day]) acc[price.day] = [];
      acc[price.day].push(price);
      return acc;
    }, {});

    return (
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Disponibilités et tarifs</h2>
          </div>
        </div>
        <AnimatePresence>
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mt-4 space-y-4">
              {availability.length > 0 ? (
                availability.map((day: string) => (
                  <div
                    key={day}
                    className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-xl"
                  >
                    <div
                      className="flex items-center justify-between p-4 transition-colors duration-200 cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => setExpandedDay(expandedDay === day ? null : day)}
                    >
                      <div className="flex items-center space-x-3">
                        <FaCalendarAlt className="text-lg text-blue-500" />
                        <span className="font-semibold text-gray-900 dark:text-white">{day}</span>
                      </div>
                      <button className="flex items-center space-x-2 text-blue-500 transition-colors duration-200 hover:text-blue-600">
                        {expandedDay === day ? (
                          <div className="flex items-center">
                            <span className="text-sm font-medium">Fermer</span>
                            <i className="ml-1 las la-angle-down"></i>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className="text-sm font-medium">Détails</span>
                            <i className="ml-1 las la-angle-right"></i>
                          </div>
                        )}
                      </button>
                    </div>
                    {expandedDay === day && groupedPrices[day] && (
                      <div className="p-4 space-y-3 bg-white dark:bg-gray-800">
                        {groupedPrices[day]
                          .sort((a: any, b: any) => {
                            const typeOrder: Record<string, number> = {
                              heure: 1,
                              "demi-journée": 2,
                              "journée complète": 3,
                            };
                            if (typeOrder[a.type] !== typeOrder[b.type]) {
                              return typeOrder[a.type] - typeOrder[b.type];
                            }
                            return parseInt(a.startHour) - parseInt(b.startHour);
                          })
                          .map((price: any) => (
                            <div
                              key={price.id}
                              className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            >
                              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                                <FaClock className="text-sm text-blue-400" />
                                <span className="text-sm">
                                  {price.startHour} - {price.endHour} / {price.min} - {price.max}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                                <span className="text-sm font-medium">
                                  {price.price} € /{" "}
                                  {price.type === "heure"
                                    ? "heure"
                                    : price.type === "demi-journée"
                                      ? "demi-journée"
                                      : "journée complète"}
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Aucune disponibilité trouvée.</p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    );
  };

  const renderSection4 = () => {
    const availability = locationState?.availableDays || [];
    const prices = locationState?.prices || [];
    return <PriceListSection availability={availability} prices={prices} />;
  };

  const renderSection7 = () => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Localisation</h2>
      </div>
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="overflow-hidden rounded-xl ring-1 ring-gray-200">
            {lat && lng && (
              <iframe
                title="x"
                width="100%"
                height="300"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={googleMapsURL}
              ></iframe>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );

  const renderSection8 = () => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Informations supplémentaires</h2>
      </div>
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="text-gray-600 dark:text-gray-300"
        >
          <span>- Dépôt de garantie : {locationState?.guarantee} €</span>
        </motion.div>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="text-gray-600 dark:text-gray-300"
        >
          <span>- Frais de ménage : {locationState?.cleaning_costs} €</span>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );

  const renderSection11 = () => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-2xl"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Politiques d'annulation</h2>
      </div>
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="text-gray-600 dark:text-gray-300"
        >
          {locationState?.reservation_type === "Stricte" ? (
            <>
              <p>
                Stricte : Les clients peuvent annuler dans les 48 heures suivant la réservation pour un
                remboursement complet. Passé ce délai, aucune annulation n'est possible et aucun remboursement ne
                sera effectué.
              </p>
              <p>- Remboursement de 70 % de la valeur de la réservation en cas d'annulation</p>
            </>
          ) : (
            <p>
              Flexible : Les clients peuvent annuler jusqu'à 5 jours avant la date d'arrivée prévue pour un
              remboursement de 70 % du montant total. Les annulations effectuées moins de 5 jours avant la date
              d'arrivée ne sont pas remboursables.
            </p>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );

  return (
    <>
      <Helmet>
        <title>{`Event To Bee - ${locationState?.title}`}</title>
        <meta
          name="description"
          content="Découvrez les détails du l'espace à louer pour votre évènement sur Event To Bee."
        />
      </Helmet>

      {!loading ? (
        <DetailPagetLayout>
          <div className="mb-5 nc-StayDetailPageContainer">
            <header className="rounded-md sm:rounded-xl">
              <div className="relative grid grid-cols-3 gap-1 sm:grid-cols-4 sm:gap-2">
                <div
                  className="relative col-span-2 row-span-3 overflow-hidden rounded-md cursor-pointer sm:row-span-2 sm:rounded-xl"
                  onClick={handleOpenModalImageGallery}
                >
                  <img
                    className="absolute inset-0 object-cover w-full h-full rounded-md sm:rounded-xl"
                    src={process.env.REACT_APP_CLOUD_FRONT + locationState?.images[0]}
                    alt=""
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
                  />
                  <div className="absolute inset-0 transition-opacity opacity-0 bg-opacity-20 bg-neutral-900 hover:opacity-100"></div>
                </div>
                {locationState?.images.slice(0, 4).map((item: string, index: number) => (
                  <div key={index} className="relative overflow-hidden rounded-md sm:rounded-xl">
                    <div className="aspect-w-4 aspect-h-3 sm:aspect-w-6 sm:aspect-h-5">
                      <img
                        className="absolute inset-0 object-cover w-full h-full rounded-md sm:rounded-xl"
                        src={process.env.REACT_APP_CLOUD_FRONT + item}
                        alt=""
                        sizes="400px"
                      />
                    </div>
                    <div
                      className="absolute inset-0 transition-opacity opacity-0 cursor-pointer bg-opacity-20 bg-neutral-900 hover:opacity-100"
                      onClick={handleOpenModalImageGallery}
                    />
                  </div>
                ))}
                <button
                  className="absolute z-10 hidden px-4 py-2 bottom-3 left-3 rounded-xl md:flex md:items-center md:justify-center bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                  onClick={handleOpenModalImageGallery}
                >
                  <Squares2X2Icon className="w-5 h-5" />
                  <span className="ml-2 text-sm font-medium text-neutral-800">Afficher toutes les photos</span>
                </button>
              </div>
            </header>
            <main className="relative z-10 flex flex-col space-y-6 mt-11 lg:flex-row">
              <div className="w-full space-y-6">
                {renderSection1()}
                {renderSection2()}
                {locationState?.prices?.length > 0 && renderSection4()}
                {locationState?.events?.length > 0 && renderEventsSection()}
                {locationState?.playground?.length > 0 && renderPlayGroundsSection()}
                {locationState?.equipments?.length > 0 && renderSection3()}
                {locationState?.extras?.length > 0 && renderSection10()}
                {locationState?.conditions?.length > 0 && renderSection9()}
                {renderSection5()}
                {renderSection7()}
                {renderSection8()}
                {renderSection11()}
              </div>
            </main>
          </div>
        </DetailPagetLayout>
      ) : (
        <div className="flex items-center justify-center h-screen">
          <DetailLoader icon={FaHome} />
        </div>
      )}
    </>
  );
};

export default StayDetailPageContainer;
